import { useApi } from '../api/context';
import { useAsync } from '../api/useAsync';
import { Page, PageHeader, Card, Loading, ErrorState } from '../components/common';
import { FrameworkImport } from '../components/FrameworkImport';
import { FrameworkFetch } from '../components/FrameworkFetch';
import { LicenceToggle } from '../components/LicenceToggle';
import { FrameworkGuide } from '../components/FrameworkGuide';
import { FrameworkDerive } from '../components/FrameworkDerive';
import { Link } from 'react-router-dom';
import {
  makeStyles, tokens, Text, Badge,
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
  MessageBar, MessageBarBody,
} from '@fluentui/react-components';

const useStyles = makeStyles({
  link: { color: tokens.colorBrandForeground1, textDecorationLine: 'none' },
  tableWrap: { width: '100%', overflowX: 'auto', minWidth: 0 },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  stack: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalM },
});

export function Frameworks() {
  const styles = useStyles();
  const api = useApi();
  const frameworks = useAsync(() => api.getFrameworks(), []);
  const sources = useAsync(() => api.getFrameworkSources(), []);
  const derivable = useAsync(() => api.getDerivable(), []);

  if (frameworks.loading) return <Loading />;
  if (frameworks.error) return <ErrorState error={frameworks.error} />;

  const rows = frameworks.data ?? [];
  // Three distinct states, previously collapsed into one sentence:
  //   a licence is needed and none has been asserted
  //   a licence has been asserted but the content has not been uploaded
  //   content is loaded
  const awaitingLicence = rows.filter(
    (f) => f.requiresLicence && !f.commercialLicenceAsserted && !f.contentLoaded);
  const awaitingUpload = rows.filter(
    (f) => f.requiresLicence && f.commercialLicenceAsserted && !f.contentLoaded);
  const noVersion = rows.filter((f) => !f.version);

  return (
    <Page>
      <PageHeader
        title="Frameworks"
        description="What this assessment can be measured against, and how far each mapping can be trusted."
      />

      {awaitingLicence.length > 0 && (
        <MessageBar intent="info">
          <MessageBarBody>
            {awaitingLicence.length} framework{awaitingLicence.length > 1 ? 's need' : ' needs'} a
            licence from the publisher before the content may be loaded. Mappings still
            resolve; only the control titles are missing. Record a licence with the button
            beside the framework, then import your own copy.
          </MessageBarBody>
        </MessageBar>
      )}

      {awaitingUpload.length > 0 && (
        <MessageBar intent="warning">
          <MessageBarBody>
            A licence is recorded for {awaitingUpload.map((f) => f.name).join(', ')}, but no
            content has been uploaded yet. Asserting a licence permits the import; it does
            not perform it.
          </MessageBarBody>
        </MessageBar>
      )}

      {noVersion.length > 0 && (
        <MessageBar intent="warning">
          <MessageBarBody>
            {noVersion.length} framework{noVersion.length > 1 ? 's have' : ' has'} no version
            established. Each is published as a PDF with no machine readable release, so the
            version has to be checked against the publisher by hand. Until that is done they
            should not appear in a customer deliverable.
          </MessageBarBody>
        </MessageBar>
      )}

      <Card>
        <div className={styles.stack}>
          <div className={styles.tableWrap}>
          <Table aria-label="Frameworks" size="medium">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Framework</TableHeaderCell>
                <TableHeaderCell>Jurisdiction</TableHeaderCell>
                <TableHeaderCell>Content</TableHeaderCell>
                <TableHeaderCell>Version checked</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((f) => (
                <TableRow key={f.frameworkId}>
                  <TableCell>
                    <div>
                      {/* Linked only where content exists. A link to an empty
                          page is a small dishonesty the reader pays for. */}
                      {f.contentLoaded ? (
                        <Link to={`/frameworks/${f.frameworkId}`} className={styles.link}>
                          <Text weight="semibold">{f.name}</Text>
                        </Link>
                      ) : (
                        <Text weight="semibold">{f.name}</Text>
                      )}{' '}
                      <FrameworkGuide frameworkId={f.frameworkId} name={f.name} />{' '}
                      {f.isFallbackTier && (
                        <Badge appearance="tint" color="brand" size="small">Fallback</Badge>
                      )}
                      <div className={styles.muted}>
                        {f.version
                          ? `${f.version}${f.versionSource === 'secondary' ? ' (second hand)' : ''}`
                          : 'version not established'} · {f.publisher} · {f.licenceCode}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={styles.muted}>
                      {f.jurisdiction}
                      {f.sector ? ` · ${f.sector}` : ''}
                    </span>
                  </TableCell>
                  <TableCell>
                    {f.contentLoaded ? (
                      <Badge appearance="tint" color="success">
                        {f.controlCount} controls
                      </Badge>
                    ) : f.requiresLicence ? (
                      <Badge appearance="tint" color="warning">Licence required</Badge>
                    ) : (
                      <Badge appearance="tint" color="subtle">Not imported</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {f.versionVerifiedUtc ? (
                      <span className={styles.muted}>{f.versionVerifiedUtc}</span>
                    ) : f.versionSource === 'secondary' ? (
                      /* Sourced, but from somewhere other than the publisher.
                         Weaker than verified, stronger than nothing, and the
                         difference is worth showing. */
                      <Badge appearance="tint" color="warning" size="small">Second hand</Badge>
                    ) : (
                      <Badge appearance="tint" color="danger" size="small">No version</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {/* Only offered where an importer exists. Listing an upload
                        button for a framework nothing can read would waste the
                        reader's time. */}
                    {/* Fetch where the publisher allows it, upload where the
                        licence requires an operator to assert they hold it. */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      {sources.data?.some((s) => s.frameworkId === f.frameworkId && s.autoFetch) ? (
                        <FrameworkFetch framework={f} onFetched={frameworks.reload} />
                      ) : f.frameworkId === 'cis-controls-v8' ? (
                        <FrameworkImport framework={f} onImported={frameworks.reload} />
                      ) : derivable.data?.some((d) => d.frameworkId === f.frameworkId) ? (
                        /* No publisher feed exists, so the SCF's cross mappings
                           are the only route to a section list. */
                        <FrameworkDerive framework={f} onDerived={frameworks.reload} />
                      ) : null}
                      {/* Only where the publisher's licence actually gates something. */}
                      {(f.requiresLicence || f.licenceCode.includes('nd')) && (
                        <LicenceToggle framework={f} onChanged={frameworks.reload} />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </div>
      </Card>
    </Page>
  );
}
